import React from "react";
import { Chip, Tooltip } from "@mui/material";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";

type ChipColor =
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning";

interface Meta {
  label: string;
  color: ChipColor;
  icon: React.ReactElement;
  hint: string;
}

// Lifecycle status -> human label, colour, icon, and a one-line explanation.
// Note: the suite status is the *orchestration* lifecycle, NOT the test outcome
// (a suite can be "Completed" with 0% passed) — the tooltip spells that out.
const STATUS_META: Record<string, Meta> = {
  INIT: {
    label: "Pending",
    color: "default",
    icon: <ScheduleOutlinedIcon />,
    hint: "Created but not started yet.",
  },
  PREPARE_EXECUTION: {
    label: "Preparing",
    color: "info",
    icon: <HourglassEmptyIcon />,
    hint: "Fetching framework and cases before the run.",
  },
  IN_PROGRESS: {
    label: "Running",
    color: "info",
    icon: <AutorenewIcon />,
    hint: "Cases are executing on an agent.",
  },
  SUCCESS: {
    label: "Completed",
    color: "success",
    icon: <CheckCircleOutlineIcon />,
    hint: "The run finished. Check TC Stat for the pass rate.",
  },
  SUCCESS_NOTIFY: {
    label: "Completed",
    color: "success",
    icon: <CheckCircleOutlineIcon />,
    hint: "The run finished. Check TC Stat for the pass rate.",
  },
  FINISHED: {
    label: "Completed",
    color: "success",
    icon: <CheckCircleOutlineIcon />,
    hint: "The run finished. Check TC Stat for the pass rate.",
  },
  FAILED: {
    label: "Failed",
    color: "error",
    icon: <ErrorOutlineIcon />,
    hint: "The run did not complete successfully.",
  },
  ERROR: {
    label: "Error",
    color: "error",
    icon: <ReportProblemOutlinedIcon />,
    hint: "An error occurred while running the suite.",
  },
  ABORTED: {
    label: "Aborted",
    color: "warning",
    icon: <BlockOutlinedIcon />,
    hint: "The run was stopped before finishing.",
  },
};

const DRAFT_META: Meta = {
  label: "Draft",
  color: "warning",
  icon: <EditNoteOutlinedIcon />,
  hint: "Unfinished — resume to add cases and run it.",
};

interface SuiteStatusChipProps {
  status: string;
  draft?: boolean;
  passed?: number;
  total?: number;
}

const SuiteStatusChip: React.FC<SuiteStatusChipProps> = ({
  status,
  draft = false,
  passed,
  total,
}) => {
  const meta = draft
    ? DRAFT_META
    : STATUS_META[status] || {
        label: status || "Unknown",
        color: "default" as ChipColor,
        icon: <ScheduleOutlinedIcon />,
        hint: "",
      };

  // For finished runs, append the real outcome so "Completed" isn't mistaken
  // for "all passed".
  const finished = ["SUCCESS", "SUCCESS_NOTIFY", "FINISHED", "FAILED"].includes(
    status
  );
  const outcome =
    finished && typeof total === "number" && total > 0
      ? ` · ${passed ?? 0}/${total} passed`
      : "";

  return (
    <Tooltip arrow title={`${meta.hint}${outcome}`}>
      <Chip
        icon={React.cloneElement(meta.icon, { sx: { fontSize: 15 } })}
        variant={draft ? "outlined" : "filled"}
        color={meta.color}
        label={meta.label}
        sx={{
          minWidth: 116,
          height: 26,
          fontSize: "0.72rem",
          fontWeight: 700,
          "& .MuiChip-icon": { ml: 0.5 },
        }}
      />
    </Tooltip>
  );
};

export default SuiteStatusChip;
